import React, { useEffect, useRef, useState } from 'react';
import { Spin, Table, Input, Button } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getUnsettledSalePaymentsAction,
  updateUnsettledSalePaymentsAction,
  updateSalePaymentSplitAction,
} from '../../Sales/store/salesActions';

interface SalePayment {
  saleNumber: string | undefined;
  cashier: string;
  transactionDate: string;
  description: string;
  notes?: string;
  branch: string;
  paymentType: string;
  _id: string;
  principal: number | undefined;
  onlinePrincipal?: number;
  offlinePrincipal?: number;
  OtherTransaction?: {
    Amount: number;
    Description: string;
  };
}

export default function SalePaymentsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const { unsettledPayments }: any = useSelector<any>((state) => state?.sales);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [onlinePrincipalValue, setOnlinePrincipalValue] = useState<string>('');
  const [offlinePrincipalValue, setOfflinePrincipalValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const processingRef = useRef(false);

  const clearEdit = () => {
    setEditingId(null);
    setOnlinePrincipalValue('');
    setOfflinePrincipalValue('');
  };

  const getSalePayments = async (): Promise<void> => {
    await getUnsettledSalePaymentsAction(dispatch, {});
    processingRef.current = false;
    setSaving(false);
    clearEdit();
  };

  useEffect(() => {
    void getSalePayments();
  }, []);

  const handleEdit = (record: SalePayment) => {
    setEditingId(record._id);
    setOnlinePrincipalValue(String(record.onlinePrincipal ?? ''));
    setOfflinePrincipalValue(String(record.offlinePrincipal ?? ''));
  };

  // The split must reconcile to the total the payment was raised with
  const isPrincipalSplitValid = (record: SalePayment) =>
    Math.abs(
      Number(onlinePrincipalValue) +
        Number(offlinePrincipalValue) -
        Number(record.principal ?? 0)
    ) <= 0.009;

  const handleSave = async (record: SalePayment) => {
    if (!isPrincipalSplitValid(record)) {
      alert('Online Principal + Offline Principal must match Principal amount');
      return;
    }

    setSaving(true);
    await updateSalePaymentSplitAction(dispatch, {
      id: record._id,
      onlinePrincipal: Number(onlinePrincipalValue),
      offlinePrincipal: Number(offlinePrincipalValue),
    });
    await getSalePayments();
  };

  const handleUpdate = async (salePayment: SalePayment, status: string) => {
    try {
      await updateUnsettledSalePaymentsAction(dispatch, {
        id: salePayment._id,
        status,
      });
      void getSalePayments();
    } catch (error) {
      console.error('Error updating payment:', error);
      processingRef.current = false;
    } finally {
      processingRef.current = false;
    }
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: SalePayment, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Sale Number',
      dataIndex: 'saleNumber',
      key: 'saleNumber',
      render: (_: any, record: SalePayment) => <p>{record.saleNumber}</p>,
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      render: (_: any, record: SalePayment) => <p>{record.principal}</p>,
    },
    {
      title: 'Online Principal',
      dataIndex: 'onlinePrincipal',
      key: 'onlinePrincipal',
      render: (_: any, record: SalePayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlinePrincipalValue}
            onChange={(e) => {
              setOnlinePrincipalValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlinePrincipal}</p>
        ),
    },
    {
      title: 'Offline Principal',
      dataIndex: 'offlinePrincipal',
      key: 'offlinePrincipal',
      render: (_: any, record: SalePayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlinePrincipalValue}
            onChange={(e) => {
              setOfflinePrincipalValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlinePrincipal}</p>
        ),
    },
    {
      title: 'Edit',
      dataIndex: 'edit',
      key: 'edit',
      width: 120,
      render: (_: any, record: SalePayment) =>
        editingId === record._id ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              icon={<SaveOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              loading={saving}
              disabled={!isPrincipalSplitValid(record)}
              onClick={() => {
                void handleSave(record);
              }}
            ></Button>
            <Button
              icon={<CloseOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              disabled={saving}
              onClick={clearEdit}
            ></Button>
          </div>
        ) : (
          <Button
            icon={<EditOutlined style={{ color: '#000' }} />}
            size="small"
            type="link"
            onClick={() => {
              handleEdit(record);
            }}
          ></Button>
        ),
    },
    {
      title: 'Cash received',
      dataIndex: 'actions',
      width: 200,
      render: (_: any, record: SalePayment) => {
        const settleDisabled = processingRef.current || editingId === record._id;
        return (
          <button
            className="search__button"
            style={{
              background: !settleDisabled ? '#27ae60' : '#b2b2b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 24px',
              fontWeight: 700,
              cursor: !settleDisabled ? 'pointer' : 'not-allowed',
              opacity: !settleDisabled ? 1 : 0.7,
            }}
            disabled={settleDisabled}
            onClick={() => {
              if (!settleDisabled) {
                processingRef.current = true;
                void handleUpdate(record, 'cash_received');
              }
            }}
          >
            Cash received
          </button>
        );
      },
    },
    {
      title: 'Reject',
      dataIndex: 'actions2',
      width: 200,
      render: (_: any, record: SalePayment) => (
        <button
          className="search__button"
          style={{
            background: !processingRef.current ? '#e74c3c' : '#b2b2b2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 24px',
            fontWeight: 700,
            cursor: !processingRef.current ? 'pointer' : 'not-allowed',
            opacity: !processingRef.current ? 1 : 0.7,
          }}
          disabled={processingRef.current}
          onClick={() => {
            if (!processingRef.current) {
              processingRef.current = true;
              void handleUpdate(record, 'rejected');
            }
          }}
        >
          Reject
        </button>
      ),
    },
  ];

  return (
    <div className="users__table__page">
      {!(unsettledPayments.length > 0) && <Spin spinning={false} fullscreen />}
      <div style={{ width: '100%' }}>
        <Table dataSource={unsettledPayments} columns={columns} />
      </div>
    </div>
  );
}
